import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    rectSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, Button, Typography, message, Progress, Modal } from 'antd';
import { PlusOutlined, UploadOutlined, CloseOutlined, HolderOutlined } from '@ant-design/icons';
import { useBlocker, useBeforeUnload } from 'react-router-dom';
import RideAPI from 'services/ride';

const { Text } = Typography;

const MAX_IMAGES = 10;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const sortImages = (images = []) =>
    [...images].sort(
        (a, b) => (a.sort_order ?? a.sortOrder ?? 0) - (b.sort_order ?? b.sortOrder ?? 0)
    );

const normalizeImages = (images = []) =>
    sortImages(images).map((img, index) => ({
        id: img.id,
        url: img.url,
        sort_order: img.sort_order ?? img.sortOrder ?? index,
    }));

const idsEqual = (left = [], right = []) =>
    left.length === right.length && left.every((id, index) => id === right[index]);

const validateImageFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
        message.error('Please select a valid image file (JPEG, PNG, or WEBP)');
        return false;
    }
    if (file.size > MAX_FILE_SIZE) {
        message.error('File size should be less than 10MB');
        return false;
    }
    return true;
};

const SortableImageCell = ({
    image,
    index,
    onDelete,
    onReplace,
    isDeleting,
    isReplacing,
    controlsLocked,
    dndDisabled,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: image.id,
        disabled: dndDisabled,
    });

    const replaceInputRef = useRef(null);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };

    const handleReplaceSelect = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !validateImageFile(file)) {
            return;
        }
        onReplace(image.id, file);
    };

    return (
        <div ref={setNodeRef} style={style} className="ride-gallery-cell">
            <div className="ride-gallery-thumb-wrap">
                {!dndDisabled && (
                    <button
                        type="button"
                        className="ride-gallery-drag-handle"
                        aria-label={`Drag image ${index + 1}`}
                        {...attributes}
                        {...listeners}
                    >
                        <HolderOutlined />
                    </button>
                )}
                <button
                    type="button"
                    className="ride-gallery-delete"
                    aria-label={`Delete image ${index + 1}`}
                    disabled={controlsLocked || isDeleting}
                    onClick={() => onDelete(image.id)}
                >
                    <CloseOutlined />
                </button>
                <img src={image.url} alt={`Ride image ${index + 1}`} className="ride-gallery-thumb" />
            </div>
            <Button
                size="small"
                icon={<UploadOutlined />}
                loading={isReplacing}
                disabled={controlsLocked}
                onClick={() => replaceInputRef.current?.click()}
            >
                Upload
            </Button>
            <input
                type="file"
                ref={replaceInputRef}
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handleReplaceSelect}
            />
        </div>
    );
};

const RideFeaturedImageGallery = ({ rideId, rideDetails, updateRideFeaturedImages }) => {
    const [originalImagesOrder, setOriginalImagesOrder] = useState([]);
    const [currentImagesOrder, setCurrentImagesOrder] = useState([]);
    const [isLoadingGallery, setIsLoadingGallery] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [replacingImageId, setReplacingImageId] = useState(null);
    const [deletingImageId, setDeletingImageId] = useState(null);
    const [isSavingOrder, setIsSavingOrder] = useState(false);

    const addInputRef = useRef(null);
    const blockerModalShownRef = useRef(false);

    const isReorderDirty = useMemo(
        () => !idsEqual(
            originalImagesOrder.map((img) => img.id),
            currentImagesOrder.map((img) => img.id)
        ),
        [originalImagesOrder, currentImagesOrder]
    );

    const isReplacing = Boolean(replacingImageId);
    const dndDisabled = isUploading || isReplacing || isSavingOrder;
    const controlsLocked = isReorderDirty || isUploading || isReplacing || isSavingOrder;
    const remainingSlots = MAX_IMAGES - currentImagesOrder.length;

    const rideTicketImageURL = rideDetails?.imageURL || rideDetails?.defaultImageURL || null;

    const syncFromResponse = useCallback((data) => {
        const normalized = normalizeImages(data?.featuredImages);
        setOriginalImagesOrder(normalized);
        setCurrentImagesOrder(normalized);
        if (updateRideFeaturedImages) {
            updateRideFeaturedImages(rideId, {
                featuredImages: normalized,
                imageURL: data?.imageURL,
                defaultImageURL: data?.defaultImageURL ?? rideDetails?.defaultImageURL,
            });
        }
    }, [rideId, updateRideFeaturedImages, rideDetails?.defaultImageURL]);

    useEffect(() => {
        let cancelled = false;

        const initGallery = async () => {
            if (!rideId) {
                return;
            }

            const fromRideDetails = normalizeImages(rideDetails?.featuredImages);
            if (fromRideDetails.length > 0) {
                setOriginalImagesOrder(fromRideDetails);
                setCurrentImagesOrder(fromRideDetails);
                return;
            }

            setIsLoadingGallery(true);
            try {
                const response = await RideAPI.getRideFeaturedImages(rideId);
                const data = RideAPI.unwrapRideApiData(response);
                if (!cancelled && data?.featuredImages?.length) {
                    syncFromResponse(data);
                    return;
                }
            } catch (error) {
                console.error('Failed to load featured images:', error);
            } finally {
                if (!cancelled) {
                    setIsLoadingGallery(false);
                }
            }

            if (!cancelled) {
                setOriginalImagesOrder([]);
                setCurrentImagesOrder([]);
            }
        };

        initGallery();

        return () => {
            cancelled = true;
        };
    }, [rideId, rideDetails?.featuredImages, syncFromResponse]);

    useEffect(() => {
        if (isReorderDirty || isUploading || isReplacing || isSavingOrder) {
            return;
        }
        const fromRideDetails = normalizeImages(rideDetails?.featuredImages);
        if (fromRideDetails.length > 0) {
            setOriginalImagesOrder(fromRideDetails);
            setCurrentImagesOrder(fromRideDetails);
        }
    }, [rideDetails?.featuredImages, isReorderDirty, isUploading, isReplacing, isSavingOrder]);

    useBeforeUnload(
        useCallback(
            (event) => {
                if (!isReorderDirty) {
                    return;
                }
                event.preventDefault();
                event.returnValue = '';
            },
            [isReorderDirty]
        )
    );

    const blocker = useBlocker(isReorderDirty);

    useEffect(() => {
        if (blocker.state !== 'blocked') {
            blockerModalShownRef.current = false;
            return;
        }
        if (blockerModalShownRef.current) {
            return;
        }
        blockerModalShownRef.current = true;

        Modal.confirm({
            title: 'Unsaved image order',
            content: 'Your unsaved image order will be lost if you leave this page.',
            okText: 'Leave',
            cancelText: 'Stay',
            onOk: () => {
                blockerModalShownRef.current = false;
                blocker.proceed();
            },
            onCancel: () => {
                blockerModalShownRef.current = false;
                blocker.reset();
            },
        });
    }, [blocker]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const uploadFilesToS3 = async (files, presignItems) => {
        const progressByFile = new Array(files.length).fill(0);

        const updateAggregateProgress = () => {
            const total = progressByFile.reduce((sum, value) => sum + value, 0) / files.length;
            setUploadProgress(Math.round(total * 100));
        };

        await Promise.all(
            files.map((file, index) =>
                RideAPI.uploadFileToPresignedUrl(
                    presignItems[index].presignedUrl,
                    file,
                    (fileProgress) => {
                        progressByFile[index] = fileProgress;
                        updateAggregateProgress();
                    }
                )
            )
        );
    };

    const handleAddPhotos = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';

        if (!files.length) {
            return;
        }

        if (files.length > remainingSlots) {
            message.error(`You can add at most ${remainingSlots} more image(s).`);
            return;
        }

        for (const file of files) {
            if (!validateImageFile(file)) {
                return;
            }
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            const presignResponse = await RideAPI.presignRideFeaturedImages(
                rideId,
                files.map((file) => file.name)
            );
            const presignData = RideAPI.unwrapRideApiData(presignResponse);
            const presignItems = presignData?.files;

            if (!presignItems?.length || presignItems.length !== files.length) {
                throw new Error('Failed to prepare image uploads');
            }

            await uploadFilesToS3(files, presignItems);

            const batchResponse = await RideAPI.batchAddRideFeaturedImages(
                rideId,
                presignItems.map((item) => ({ url: item.fileUrl }))
            );
            const batchData = RideAPI.unwrapRideApiData(batchResponse);

            if (batchData) {
                syncFromResponse(batchData);
                message.success(files.length === 1 ? 'Image added successfully' : 'Images added successfully');
            }
        } catch (error) {
            console.error('Add photos error:', error);
            message.error(RideAPI.getApiErrorMessage(error));
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const handleDelete = async (imageId) => {
        setDeletingImageId(imageId);
        try {
            const response = await RideAPI.deleteRideFeaturedImage(rideId, imageId);
            const data = RideAPI.unwrapRideApiData(response);
            if (data) {
                syncFromResponse(data);
                message.success('Image deleted');
            }
        } catch (error) {
            console.error('Delete image error:', error);
            message.error(RideAPI.getApiErrorMessage(error));
        } finally {
            setDeletingImageId(null);
        }
    };

    const handleReplace = async (imageId, file) => {
        setReplacingImageId(imageId);
        try {
            const fileUrl = await RideAPI.uploadFileViaApi(file);

            const replaceResponse = await RideAPI.replaceRideFeaturedImage(
                rideId,
                imageId,
                fileUrl
            );
            const replaceData = RideAPI.unwrapRideApiData(replaceResponse);

            if (replaceData) {
                syncFromResponse(replaceData);
                message.success('Image replaced successfully');
            }
        } catch (error) {
            console.error('Replace image error:', error);
            message.error(RideAPI.getApiErrorMessage(error));
        } finally {
            setReplacingImageId(null);
        }
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }

        setCurrentImagesOrder((items) => {
            const oldIndex = items.findIndex((item) => item.id === active.id);
            const newIndex = items.findIndex((item) => item.id === over.id);
            return arrayMove(items, oldIndex, newIndex).map((img, index) => ({
                ...img,
                sort_order: index,
            }));
        });
    };

    const handleSaveOrder = async () => {
        setIsSavingOrder(true);
        try {
            const response = await RideAPI.saveRideFeaturedImagesOrder(
                rideId,
                currentImagesOrder.map(({ id }) => ({ id }))
            );
            const data = RideAPI.unwrapRideApiData(response);
            if (data) {
                syncFromResponse(data);
                message.success('Image order saved');
            }
        } catch (error) {
            console.error('Save order error:', error);
            message.error(RideAPI.getApiErrorMessage(error));
        } finally {
            setIsSavingOrder(false);
        }
    };

    const handleCancelReorder = () => {
        setCurrentImagesOrder([...originalImagesOrder]);
    };

    return (
        <Card
            className="ride-featured-image-gallery"
            style={{
                marginBottom: 24,
                borderRadius: '8px',
                width: '100%',
                backgroundColor: '#fff',
                border: '1px solid #eaeaea',
            }}
            bodyStyle={{ padding: 24 }}
            loading={isLoadingGallery}
        >
            <Text
                style={{
                    color: '#606060',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    display: 'block',
                    letterSpacing: '2px',
                }}
            >
                RIDE IMAGES (16:9)
            </Text>

            <div className="ride-gallery-preview">
                {currentImagesOrder.length === 0 ? (
                    <div className="ride-gallery-empty">No images uploaded</div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={currentImagesOrder.map((img) => img.id)}
                            strategy={rectSortingStrategy}
                        >
                            <div className="ride-gallery-grid">
                                {currentImagesOrder.map((image, index) => (
                                    <SortableImageCell
                                        key={image.id}
                                        image={image}
                                        index={index}
                                        onDelete={handleDelete}
                                        onReplace={handleReplace}
                                        isDeleting={deletingImageId === image.id}
                                        isReplacing={replacingImageId === image.id}
                                        controlsLocked={controlsLocked}
                                        dndDisabled={dndDisabled}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                )}
            </div>

            {isUploading && (
                <div style={{ marginTop: 16 }}>
                    <Progress percent={uploadProgress} status="active" />
                </div>
            )}

            <div className="ride-gallery-footer">
                <div className="ride-gallery-footer-left">
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        loading={isUploading}
                        disabled={controlsLocked || remainingSlots === 0}
                        onClick={() => addInputRef.current?.click()}
                        className="ride-gallery-btn-primary ride-gallery-footer-btn"
                    >
                        Add Photo(s){remainingSlots < MAX_IMAGES ? ` (${remainingSlots} left)` : ''}
                    </Button>
                    <Button
                        type="primary"
                        loading={isSavingOrder}
                        disabled={!isReorderDirty || isUploading || isReplacing}
                        onClick={handleSaveOrder}
                        className="ride-gallery-btn-primary ride-gallery-footer-btn"
                    >
                        Save Order
                    </Button>
                    <Button
                        disabled={!isReorderDirty || isSavingOrder}
                        onClick={handleCancelReorder}
                        className="ride-gallery-footer-btn"
                    >
                        Cancel reordering
                    </Button>
                </div>
                <div className="ride-gallery-footer-right">
                    {rideTicketImageURL ? (
                        <>
                            <img
                                src={rideTicketImageURL}
                                alt="Ride ticket image"
                                className="ride-gallery-default-thumb"
                            />
                            <Text className="ride-gallery-default-label">(RIDE TICKET IMAGE)</Text>
                        </>
                    ) : (
                        <Text type="secondary" className="ride-gallery-default-label">
                            No ride ticket image
                        </Text>
                    )}
                </div>
            </div>

            <input
                type="file"
                ref={addInputRef}
                multiple
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handleAddPhotos}
            />

            <style>{`
                .ride-featured-image-gallery .ride-gallery-btn-primary:not(:disabled) {
                    background-color: #6528D8;
                    border-color: #6528D8;
                }
                .ride-featured-image-gallery .ride-gallery-btn-primary:not(:disabled):hover {
                    background-color: #5420b8;
                    border-color: #5420b8;
                }
                .ride-featured-image-gallery .ride-gallery-preview {
                    background: #eee;
                    border-radius: 8px;
                    padding: 20px;
                    min-height: 120px;
                }
                .ride-featured-image-gallery .ride-gallery-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                    gap: 20px;
                }
                .ride-featured-image-gallery .ride-gallery-cell {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                }
                .ride-featured-image-gallery .ride-gallery-thumb-wrap {
                    position: relative;
                    width: 100%;
                    aspect-ratio: 16 / 9;
                    border-radius: 8px;
                    overflow: visible;
                }
                .ride-featured-image-gallery .ride-gallery-thumb {
                    display: block;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    border-radius: 8px;
                    border: 1px solid #eaeaea;
                    background: #fff;
                }
                .ride-featured-image-gallery .ride-gallery-delete {
                    position: absolute;
                    top: -8px;
                    right: -8px;
                    z-index: 2;
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    border: 1px solid #d9d9d9;
                    background: #fff;
                    color: #666;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    padding: 0;
                }
                .ride-featured-image-gallery .ride-gallery-delete:disabled {
                    opacity: 0.45;
                    cursor: not-allowed;
                }
                .ride-featured-image-gallery .ride-gallery-drag-handle {
                    position: absolute;
                    top: 6px;
                    left: 6px;
                    z-index: 2;
                    width: 24px;
                    height: 24px;
                    border-radius: 4px;
                    border: none;
                    background: rgba(255, 255, 255, 0.92);
                    color: #666;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: grab;
                    padding: 0;
                }
                .ride-featured-image-gallery .ride-gallery-drag-handle:disabled {
                    cursor: not-allowed;
                }
                .ride-featured-image-gallery .ride-gallery-empty {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    max-width: 480px;
                    margin: 0 auto;
                    aspect-ratio: 16 / 9;
                    color: #999;
                    border: 2px dashed #d9d9d9;
                    border-radius: 8px;
                    background: #fafafa;
                }
                .ride-featured-image-gallery .ride-gallery-footer {
                    display: flex;
                    margin-top: 20px;
                    gap: 24px;
                    min-height: 200px;
                }
                .ride-featured-image-gallery .ride-gallery-footer-left,
                .ride-featured-image-gallery .ride-gallery-footer-right {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                }
                .ride-featured-image-gallery .ride-gallery-footer-left {
                    gap: 12px;
                }
                .ride-featured-image-gallery .ride-gallery-footer-btn {
                    min-width: 220px;
                }
                .ride-featured-image-gallery .ride-gallery-default-thumb {
                    width: 100%;
                    max-width: 240px;
                    aspect-ratio: 16 / 9;
                    object-fit: cover;
                    border-radius: 8px;
                    border: 1px solid #eaeaea;
                    background: #fff;
                }
                .ride-featured-image-gallery .ride-gallery-default-label {
                    margin-top: 10px;
                    color: #999;
                    font-size: 12px;
                    font-style: italic;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
            `}</style>
        </Card>
    );
};

export default RideFeaturedImageGallery;
